/* eslint-disable */
import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast, Toaster } from "sonner";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}

/**
 * Session 60.5 · Event date/time RCA fix.
 *
 * Bug: `<input type="datetime-local">` returns LOCAL wall-clock strings with
 * no TZ marker. Storing them via `new Date(v).toISOString()` correctly
 * converts to UTC on save. But re-rendering by slicing the UTC ISO back
 * into the same input treated those UTC digits AS LOCAL — showing e.g.
 * 10pm for a 6pm-EDT event, and shifting the date on the next save.
 *
 * Fix: convert UTC ISO to a LOCAL wall-clock `YYYY-MM-DDTHH:mm` string when
 * rendering, and only convert LOCAL → UTC on save. Splitting into two
 * inputs (date + time) makes the "your local time" contract explicit.
 */
function isoToLocalParts(iso) {
  if (!iso) return { date: "", time: "" };
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return { date: "", time: "" };
    // Compose local YYYY-MM-DD and HH:mm (browser-local timezone).
    const pad = (n) => String(n).padStart(2, "0");
    const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
    return { date, time };
  } catch { return { date: "", time: "" }; }
}
function localPartsToIso(dateStr, timeStr) {
  if (!dateStr) return "";
  const t = timeStr && /^\d{1,2}:\d{2}$/.test(timeStr) ? timeStr : "00:00";
  // Construct as local-wallclock; new Date() reads it in the browser's TZ.
  const local = new Date(`${dateStr}T${t}`);
  if (Number.isNaN(local.getTime())) return "";
  return local.toISOString();
}

function EventRow({ ev, categories, onPatch, onApprove, onReject, onDelete }) {
  const [edit, setEdit] = useState(() => {
    const s = isoToLocalParts(ev.start_at);
    const e = isoToLocalParts(ev.end_at);
    return {
      title: ev.title || "",
      description: ev.description || "",
      city: ev.city || "",
      category: ev.category || "",
      featured: !!ev.featured,
      start_at: ev.start_at || "",
      end_at: ev.end_at || "",
      start_date: s.date, start_time: s.time,
      end_date: e.date,   end_time: e.time,
      venue: ev.venue || "",
      image_url: ev.image_url || "",
      // Session 60.23 · September sprint additions
      classification: ev.classification || "aggregated",
      cost: ev.cost || "unknown",
      modality: ev.modality || "in_person",
      registration_type: ev.registration_type || "",
      registration_url: ev.registration_url || "",
      organizer: ev.organizer || "",
    };
  });
  const [saving, setSaving] = useState(false);
  // Session 60.13 · Optional file upload for events that never scraped an
  // image (e.g. La Cultura's event submission). Reuses the community image
  // upload endpoint (Emergent Object Storage) and pastes the resulting URL
  // into image_url so the same PATCH pipeline persists it.
  const [uploading, setUploading] = useState(false);
  const onImagePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      // Session 60.13 · /community/uploads expects the field name `files`
      // (list of UploadFile). Returns { images: [{ image_url: "/api/community/images/…" }] }.
      fd.append("files", file);
      const r = await api.post("/community/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = r.data?.images?.[0]?.image_url;
      if (url) {
        setEdit(s => ({ ...s, image_url: url }));
        toast.success("Image uploaded — click Save changes to attach it.");
      } else {
        toast.error("Upload succeeded but no URL returned.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = ""; // reset input so the same file can be picked again
    }
  };

  // Recompute the ISO whenever the split fields change. Keeps a single UTC
  // ISO as the source of truth for dirty-check + PATCH payload.
  const updateStart = (patch) => setEdit(s => {
    const next = { ...s, ...patch };
    next.start_at = localPartsToIso(next.start_date, next.start_time);
    return next;
  });
  const updateEnd = (patch) => setEdit(s => {
    const next = { ...s, ...patch };
    next.end_at = next.end_date ? localPartsToIso(next.end_date, next.end_time) : "";
    return next;
  });

  const dirty =
    edit.title !== (ev.title || "") ||
    edit.description !== (ev.description || "") ||
    edit.city !== (ev.city || "") ||
    edit.category !== (ev.category || "") ||
    edit.featured !== !!ev.featured ||
    edit.start_at !== (ev.start_at || "") ||
    edit.end_at !== (ev.end_at || "") ||
    edit.venue !== (ev.venue || "") ||
    edit.image_url !== (ev.image_url || "") ||
    edit.classification !== (ev.classification || "aggregated") ||
    edit.cost !== (ev.cost || "unknown") ||
    edit.modality !== (ev.modality || "in_person") ||
    edit.registration_type !== (ev.registration_type || "") ||
    edit.registration_url !== (ev.registration_url || "") ||
    edit.organizer !== (ev.organizer || "");

  const save = async () => {
    setSaving(true);
    try {
      // Only send the fields the PATCH endpoint expects. Split date/time
      // are UI-only — server keeps a single UTC ISO per boundary.
      const { start_date, start_time, end_date, end_time, ...payload } = edit;
      await onPatch(ev.event_id, payload);
      toast.success("Event updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="m-admin-event-row" data-testid={`admin-event-${ev.event_id}`}>
      <div className="m-admin-event-img">
        {edit.image_url ? <img src={edit.image_url} alt="" /> : <div className="m-admin-event-img-empty">no image</div>}
      </div>
      <div className="m-admin-event-body">
        <div className="m-admin-event-header">
          <span className={`m-admin-event-status m-admin-event-status-${ev.status}`}>{ev.status}</span>
          <span className="m-admin-event-source">from {ev.source || "unknown"}</span>
          {ev.source_url && <a href={ev.source_url} target="_blank" rel="noopener noreferrer" className="m-admin-event-src-link">Open source →</a>}
        </div>
        <div className="m-admin-event-grid">
          <label className="m-field"><span>Title</span>
            <input value={edit.title} onChange={(e) => setEdit(s => ({...s, title: e.target.value}))} data-testid={`admin-event-title-${ev.event_id}`} />
          </label>
          <label className="m-field"><span>Category</span>
            <select value={edit.category} onChange={(e) => setEdit(s => ({...s, category: e.target.value}))} data-testid={`admin-event-cat-${ev.event_id}`}>
              <option value="">— pick —</option>
              {categories.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </label>
          <label className="m-field"><span>Classification</span>
            <select value={edit.classification} onChange={(e) => setEdit(s => ({...s, classification: e.target.value}))} data-testid={`admin-event-classification-${ev.event_id}`}>
              <option value="aggregated">Discovered Event</option>
              <option value="partner">Partner Event</option>
              <option value="metier_presents">Métier Presents</option>
            </select>
          </label>
          <label className="m-field"><span>Cost</span>
            <select value={edit.cost} onChange={(e) => setEdit(s => ({...s, cost: e.target.value}))} data-testid={`admin-event-cost-${ev.event_id}`}>
              <option value="unknown">Unknown</option>
              <option value="free">Free</option>
              <option value="paid">Paid</option>
            </select>
          </label>
          <label className="m-field"><span>Format</span>
            <select value={edit.modality} onChange={(e) => setEdit(s => ({...s, modality: e.target.value}))} data-testid={`admin-event-modality-${ev.event_id}`}>
              <option value="in_person">In person</option>
              <option value="virtual">Virtual</option>
              <option value="hybrid">Hybrid</option>
            </select>
          </label>
          <label className="m-field"><span>Registration type</span>
            <select value={edit.registration_type} onChange={(e) => setEdit(s => ({...s, registration_type: e.target.value}))} data-testid={`admin-event-regtype-${ev.event_id}`}>
              <option value="">— auto —</option>
              <option value="metier">Métier native</option>
              <option value="external">External</option>
            </select>
          </label>
          <label className="m-field" style={{gridColumn:"span 2"}}><span>Registration URL (external)</span>
            <input value={edit.registration_url} onChange={(e) => setEdit(s => ({...s, registration_url: e.target.value}))} placeholder="https://…" data-testid={`admin-event-regurl-${ev.event_id}`} />
          </label>
          <label className="m-field"><span>Organizer</span>
            <input value={edit.organizer} onChange={(e) => setEdit(s => ({...s, organizer: e.target.value}))} data-testid={`admin-event-organizer-${ev.event_id}`} />
          </label>
          <label className="m-field"><span>Location</span>
            <input value={edit.city} onChange={(e) => setEdit(s => ({...s, city: e.target.value}))} data-testid={`admin-event-city-${ev.event_id}`} placeholder="Massachusetts / New Hampshire / Virtual / …" />
          </label>
          <label className="m-field"><span>Start date</span>
            <input
              type="date"
              value={edit.start_date}
              onChange={(e) => updateStart({ start_date: e.target.value })}
              data-testid={`admin-event-start-date-${ev.event_id}`}
              style={{fontFamily: "inherit"}}
            />
          </label>
          <label className="m-field"><span>Start time (your local)</span>
            <input
              type="time"
              value={edit.start_time}
              onChange={(e) => updateStart({ start_time: e.target.value })}
              data-testid={`admin-event-start-time-${ev.event_id}`}
              style={{fontFamily: "inherit"}}
            />
          </label>
          <label className="m-field"><span>End date (optional, multi-day)</span>
            <input
              type="date"
              value={edit.end_date}
              onChange={(e) => updateEnd({ end_date: e.target.value })}
              data-testid={`admin-event-end-date-${ev.event_id}`}
              style={{fontFamily: "inherit"}}
            />
          </label>
          <label className="m-field"><span>End time (your local)</span>
            <input
              type="time"
              value={edit.end_time}
              onChange={(e) => updateEnd({ end_time: e.target.value })}
              data-testid={`admin-event-end-time-${ev.event_id}`}
              style={{fontFamily: "inherit"}}
            />
            {!edit.start_at && (
              <span style={{color: "#B44", fontSize: 11, marginTop: 4, display: "block"}}>
                Missing start date. Event will not display on /events until set.
              </span>
            )}
          </label>
          <label className="m-field m-admin-event-full"><span>Venue</span>
            <input value={edit.venue} onChange={(e) => setEdit(s => ({...s, venue: e.target.value}))} />
          </label>
          <label className="m-field m-admin-event-full"><span>Description</span>
            <textarea rows={3} value={edit.description} onChange={(e) => setEdit(s => ({...s, description: e.target.value}))} />
          </label>
          <label className="m-field m-admin-event-featured">
            <input type="checkbox" checked={edit.featured} onChange={(e) => setEdit(s => ({...s, featured: e.target.checked}))} data-testid={`admin-event-featured-${ev.event_id}`} />
            <span>Featured on /events</span>
          </label>
          <label className="m-field m-admin-event-full">
            <span>Cover image (URL or upload) &middot; 1200&times;630 works best</span>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <input
                type="url"
                value={edit.image_url}
                onChange={(e) => setEdit(s => ({...s, image_url: e.target.value}))}
                placeholder="https://…"
                data-testid={`admin-event-image-url-${ev.event_id}`}
                style={{ flex: 1, minWidth: 240 }}
              />
              <label
                className="m-btn-ghost"
                style={{ padding: "8px 14px", fontSize: 12, cursor: uploading ? "not-allowed" : "pointer", opacity: uploading ? 0.6 : 1, whiteSpace: "nowrap" }}
                data-testid={`admin-event-image-upload-${ev.event_id}`}
              >
                {uploading ? "Uploading…" : "Upload photo"}
                <input
                  type="file"
                  accept="image/*"
                  onChange={onImagePick}
                  disabled={uploading}
                  style={{ display: "none" }}
                />
              </label>
              {edit.image_url && (
                <button
                  type="button"
                  className="m-btn-ghost"
                  onClick={() => setEdit(s => ({...s, image_url: ""}))}
                  data-testid={`admin-event-image-clear-${ev.event_id}`}
                  style={{ padding: "8px 12px", fontSize: 12 }}
                >
                  Remove
                </button>
              )}
            </div>
          </label>
        </div>
        <div className="m-admin-event-actions">
          <button className="m-btn-ghost" onClick={save} disabled={!dirty || saving} data-testid={`admin-event-save-${ev.event_id}`}>{saving ? "Saving…" : "Save changes"}</button>
          {ev.status !== "published" && <button className="m-btn-gold" onClick={() => onApprove(ev.event_id)} data-testid={`admin-event-approve-${ev.event_id}`}>Approve + publish</button>}
          {ev.status !== "rejected" && ev.status !== "published" && <button className="m-btn-ghost" onClick={() => onReject(ev.event_id)} data-testid={`admin-event-reject-${ev.event_id}`}>Reject</button>}
          <button className="m-btn-ghost m-admin-event-delete" onClick={() => onDelete(ev.event_id)} data-testid={`admin-event-delete-${ev.event_id}`}>Delete</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminEvents() {
  const [status, setStatus] = useState("pending");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [showBulk, setShowBulk] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/events", { params: status === "all" ? {} : { status } });
      setEvents(r.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  useEffect(() => { api.get("/events/categories").then(r => setCategories(r.data || [])); }, []);

  const doImport = async (e) => {
    e.preventDefault();
    if (!importUrl.trim()) return;
    setImporting(true);
    try {
      const r = await api.post("/admin/events/import", { url: importUrl.trim() });
      if (r.data.already_imported) toast.info("That URL was already imported.");
      else toast.success("Event imported, review below.");
      setImportUrl("");
      setStatus("pending");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally { setImporting(false); }
  };

  const patch = async (id, changes) => {
    await api.patch(`/admin/events/${id}`, changes);
    await load();
  };
  const approve = async (id) => {
    try {
      await api.post(`/admin/events/${id}/approve`);
      // Session 57 · one-click verify — a Sonner toast with an inline action
      // link that opens the live events page in a new tab so the admin can
      // confirm the event surfaced without leaving this list.
      toast.success("Event published", {
        description: "Verify it now on the live events page.",
        action: {
          label: "Open live events →",
          onClick: () => window.open("/events", "_blank", "noopener"),
        },
        duration: 8000,
      });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
  };
  const reject = async (id) => {
    await api.post(`/admin/events/${id}/reject`);
    toast.info("Event rejected");
    await load();
  };
  const del = async (id) => {
    if (!window.confirm("Delete this event permanently?")) return;
    await api.delete(`/admin/events/${id}`);
    toast.info("Deleted");
    await load();
  };

  return (
    <div className="m-admin-events" data-testid="admin-events">
      <Toaster richColors position="top-center" />
      <div className="m-card">
        <span className="m-eyebrow" style={{color: "var(--gold)"}}>Semi-automated event aggregation</span>
        <h2 className="rec-h2" style={{margin: "6px 0 12px"}}>Import from any source.</h2>
        <p className="m-muted" style={{maxWidth: 640, marginTop: -4}}>
          Paste a URL from Meetup, Eventbrite, Luma, Chamber calendars, university .edu pages, or any incubator, Métier scrapes title, image, date, and organizer, auto-suggests a category, and stages it as pending for your review.
        </p>
        <form onSubmit={doImport} className="m-admin-import-form" data-testid="admin-events-import-form">
          <input
            type="url"
            placeholder="https://www.eventbrite.com/e/… or https://www.meetup.com/…"
            value={importUrl}
            onChange={(e) => setImportUrl(e.target.value)}
            className="m-admin-import-input"
            required
            data-testid="admin-events-import-url"
          />
          <button type="submit" className="m-btn-gold" disabled={importing} data-testid="admin-events-import-submit">
            {importing ? "Importing…" : "Import event →"}
          </button>
          <button type="button" onClick={() => setShowBulk(true)}
            data-testid="admin-events-bulk-open"
            style={{marginLeft:10, padding:"10px 18px", background:"transparent", color:"#1B2340", border:"1px solid rgba(27,35,64,0.25)", borderRadius:999, cursor:"pointer", fontSize:13}}>
            Bulk paste…
          </button>
        </form>
      </div>

      {showBulk && (
        <BulkEventImportModal
          categories={categories}
          onClose={() => setShowBulk(false)}
          onDone={async () => { setShowBulk(false); setStatus("pending"); await load(); }}
        />
      )}

      <div className="m-admin-event-tabs">
        {["pending", "published", "rejected", "all"].map(s => (
          <button
            key={s}
            className={`m-admin-event-tab ${status === s ? "on" : ""}`}
            onClick={() => setStatus(s)}
            data-testid={`admin-events-tab-${s}`}
          >{s}</button>
        ))}
        <span className="m-muted small" style={{marginLeft: "auto"}}>{events.length} event{events.length === 1 ? "" : "s"}</span>
      </div>

      {(() => {
        const hiddenNoDate = events.filter(e => e.status === "published" && !e.start_at);
        if (hiddenNoDate.length === 0) return null;
        return (
          <div
            data-testid="admin-events-missing-date-banner"
            style={{
              margin: "12px 0 16px",
              padding: "12px 16px",
              background: "#FFF3D6",
              border: "1px solid #B8913A",
              borderRadius: 10,
              color: "#7C5B10",
              fontSize: 13,
            }}
          >
            <strong>⚠ {hiddenNoDate.length} published event{hiddenNoDate.length === 1 ? "" : "s"} won&#39;t display on /events</strong> —
            they are missing a start date. The importer couldn&#39;t extract a date from the source URL. Set a start date/time on each row below to make them visible.
          </div>
        );
      })()}

      {loading ? (
        <div className="m-loading-inline">Loading…</div>
      ) : events.length === 0 ? (
        <div className="m-empty">No events in this queue yet.</div>
      ) : (
        <div className="m-admin-event-list" data-testid="admin-events-list">
          {events.map(ev => (
            <EventRow
              key={ev.event_id}
              ev={ev}
              categories={categories}
              onPatch={patch}
              onApprove={approve}
              onReject={reject}
              onDelete={del}
            />
          ))}
        </div>
      )}
    </div>
  );
}


// Session 45.3 — Bulk paste modal for importing multiple event URLs at once.
function BulkEventImportModal({ categories, onClose, onDone }) {
  const [urls, setUrls] = useState("");
  const [city, setCity] = useState("");
  const [category, setCategory] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  const detected = (urls.match(/https?:\/\/[^\s<>"']+/gi) || [])
    .map(u => u.replace(/[.,;)\]}]+$/, ""));
  const uniqueCount = new Set(detected).size;

  const submit = async () => {
    setBusy(true); setErr(""); setResult(null);
    try {
      const r = await api.post("/admin/events/import/bulk", {
        urls,
        city: city.trim() || null,
        category: category || null,
      });
      setResult(r.data);
      toast.success(`Bulk import: ${r.data.counts.imported} imported · ${r.data.counts.duplicates} dupes · ${r.data.counts.failed} failed`);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Bulk import failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div onClick={() => !busy && onClose()} data-testid="bulk-event-modal-backdrop"
      style={{position:"fixed", inset:0, background:"rgba(27,35,64,0.55)", zIndex:2100, display:"flex", alignItems:"center", justifyContent:"center", padding:24}}>
      <div onClick={(e) => e.stopPropagation()} data-testid="bulk-event-modal"
        style={{background:"#FAF8F3", borderRadius:14, width:"min(760px,100%)", maxHeight:"90vh", overflow:"auto", padding:"26px 30px", border:"1px solid rgba(184,145,58,0.35)", boxShadow:"0 30px 80px rgba(0,0,0,0.28)", color:"#1B2340", fontFamily:"Jost, sans-serif"}}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:14}}>
          <div>
            <div style={{fontSize:11, letterSpacing:"0.14em", textTransform:"uppercase", color:"#B8913A", fontWeight:600}}>Admin · Events</div>
            <h2 style={{fontFamily:"Cormorant Garamond, serif", fontSize:26, margin:"2px 0 0", fontWeight:500}}>Bulk Import</h2>
            <div style={{fontSize:12, color:"rgba(27,35,64,0.65)", marginTop:4, maxWidth:520}}>
              Paste up to 50 individual event URLs. Category listing pages (like <code>/d/…/networking/</code>) are anti-scraped by Eventbrite — paste the individual <code>/e/…</code> event links instead.
            </div>
          </div>
          <button type="button" onClick={onClose} disabled={busy} data-testid="bulk-event-close"
            style={{background:"none", border:"none", fontSize:22, cursor:"pointer", color:"rgba(27,35,64,0.6)"}}>×</button>
        </div>

        {!result && (
          <>
            <label style={{fontSize:12, color:"rgba(27,35,64,0.7)", display:"block", marginBottom:4}}>Event URLs</label>
            <textarea value={urls} onChange={(e) => setUrls(e.target.value)}
              rows={9}
              placeholder={"https://www.eventbrite.com/e/founder-friday-boston-…\nhttps://www.meetup.com/nh-tech-meetup/events/…\nhttps://lu.ma/…"}
              data-testid="bulk-event-urls"
              style={{width:"100%", padding:"12px 14px", border:"1px solid rgba(27,35,64,0.22)", borderRadius:10, fontSize:13, fontFamily:"ui-monospace, SFMono-Regular, Menlo, monospace", resize:"vertical", background:"#fff"}} />
            <div style={{fontSize:11, color:"rgba(27,35,64,0.55)", marginTop:4}} data-testid="bulk-event-count">
              {uniqueCount > 0 ? `${uniqueCount} unique URL${uniqueCount === 1 ? "" : "s"} detected` : "Commas, spaces, or newlines are all fine."}
            </div>

            <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginTop:14}}>
              <label style={{fontSize:12, color:"rgba(27,35,64,0.7)"}}>
                Force city <span style={{color:"rgba(27,35,64,0.4)"}}>(optional)</span>
                <input value={city} onChange={(e) => setCity(e.target.value)}
                  placeholder="e.g. New Hampshire"
                  data-testid="bulk-event-city"
                  style={{width:"100%", padding:"9px 10px", marginTop:4, border:"1px solid rgba(27,35,64,0.22)", borderRadius:8, fontSize:13, background:"#fff"}} />
              </label>
              <label style={{fontSize:12, color:"rgba(27,35,64,0.7)"}}>
                Force category <span style={{color:"rgba(27,35,64,0.4)"}}>(optional)</span>
                <select value={category} onChange={(e) => setCategory(e.target.value)}
                  data-testid="bulk-event-category"
                  style={{width:"100%", padding:"9px 10px", marginTop:4, border:"1px solid rgba(27,35,64,0.22)", borderRadius:8, fontSize:13, background:"#fff"}}>
                  <option value="">(auto-detect from title)</option>
                  {categories.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </label>
            </div>

            {err && <div data-testid="bulk-event-error"
              style={{background:"#FDEDED", border:"1px solid #E5A0A0", padding:"10px 14px", borderRadius:8, marginTop:14, fontSize:13}}>{err}</div>}

            <div style={{display:"flex", gap:12, justifyContent:"flex-end", marginTop:18}}>
              <button type="button" onClick={onClose} disabled={busy}
                style={{padding:"10px 18px", background:"transparent", color:"#1B2340", border:"1px solid rgba(27,35,64,0.25)", borderRadius:999, cursor:"pointer", fontSize:13}}>
                Cancel
              </button>
              <button type="button" onClick={submit} disabled={busy || uniqueCount === 0}
                data-testid="bulk-event-submit"
                style={{padding:"10px 22px", background: busy || uniqueCount === 0 ? "rgba(184,145,58,0.5)" : "#B8913A", color:"#FAF8F3", border:"none", borderRadius:999, cursor: busy || uniqueCount === 0 ? "not-allowed" : "pointer", fontSize:13, fontWeight:500}}>
                {busy ? "Importing…" : `Import ${uniqueCount || ""}`.trim()}
              </button>
            </div>
          </>
        )}

        {result && (
          <div data-testid="bulk-event-result">
            <div style={{background:"#EFF6EB", border:"1px solid #C7E0B5", borderRadius:10, padding:"14px 16px", marginBottom:14}}>
              <div style={{fontFamily:"Cormorant Garamond, serif", fontSize:20, marginBottom:4}}>
                {result.counts.total} URL{result.counts.total === 1 ? "" : "s"} processed
              </div>
              <div style={{fontSize:13, color:"rgba(27,35,64,0.75)", lineHeight:1.7}}>
                <b>{result.counts.imported}</b> imported ·{" "}
                <b>{result.counts.duplicates}</b> duplicate{result.counts.duplicates === 1 ? "" : "s"}
                {result.counts.failed > 0 && <> · <b style={{color:"#B84A3A"}}>{result.counts.failed} failed</b></>}
              </div>
            </div>

            <div style={{maxHeight:300, overflow:"auto", border:"1px solid rgba(27,35,64,0.1)", borderRadius:10, background:"#fff"}}>
              <table style={{width:"100%", borderCollapse:"collapse", fontSize:12}}>
                <thead>
                  <tr style={{background:"rgba(184,145,58,0.08)", textAlign:"left"}}>
                    <th style={{padding:"7px 10px"}}>URL</th>
                    <th style={{padding:"7px 10px"}}>Status</th>
                    <th style={{padding:"7px 10px"}}>Title / Error</th>
                  </tr>
                </thead>
                <tbody>
                  {result.results.map((r, i) => (
                    <tr key={i} style={{borderTop:"1px solid rgba(27,35,64,0.06)"}} data-testid={`bulk-event-row-${i}`}>
                      <td style={{padding:"6px 10px", fontFamily:"ui-monospace, monospace", maxWidth:280, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{r.url}</td>
                      <td style={{padding:"6px 10px"}}>
                        <span style={{
                          padding:"1px 8px", borderRadius:999, fontSize:10.5, letterSpacing:"0.04em", textTransform:"uppercase",
                          background: r.status === "imported" ? "rgba(92,138,68,0.15)" : r.status === "duplicate" ? "rgba(184,145,58,0.15)" : "rgba(184,74,58,0.15)",
                          color: r.status === "imported" ? "#4a7137" : r.status === "duplicate" ? "#8A6B26" : "#B84A3A",
                        }}>{r.status}</span>
                      </td>
                      <td style={{padding:"6px 10px"}}>
                        {r.title || <span style={{color:"#B84A3A"}}>{r.error}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{display:"flex", gap:12, justifyContent:"flex-end", marginTop:18}}>
              <button type="button" onClick={() => { setResult(null); setUrls(""); }}
                data-testid="bulk-event-another"
                style={{padding:"10px 18px", background:"transparent", color:"#1B2340", border:"1px solid rgba(27,35,64,0.25)", borderRadius:999, cursor:"pointer", fontSize:13}}>
                Import another batch
              </button>
              <button type="button" onClick={onDone}
                data-testid="bulk-event-done"
                style={{padding:"10px 22px", background:"#1B2340", color:"#FAF8F3", border:"none", borderRadius:999, cursor:"pointer", fontSize:13, fontWeight:500}}>
                Review pending queue
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
